//MockTest.jsx



import { CircularProgress } from "@material-ui/core";
import { Skeleton, Alert } from "@mui/material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import GetCourseQuiz from "../../actions/quiz/GetCourseQuiz";
import SubmitCourseQuiz from "../../actions/quiz/SubmitCourseQuiz";
import { AuthContext } from "../../context/AuthContext";
import ENDPOINT from "../../data/ENDPOINT";
import SecurityHeaders from "../../data/SecurityHeaders";
import { Helmet } from "react-helmet";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import testCompletedImage from "../../assets/test-attempted.jpg";

// Custom CSS for answer highlighting
const styles = `
  .selected-option {
    background-color: #2563eb !important;
    color: #fff !important;
    border-color: #2563eb !important;
  }
  .correct-answer {
    background-color: #28a745 !important;
    color: white !important;
    border-color: #28a745 !important;
  }
  .incorrect-answer {
    background-color: #dc3545 !important;
    color: white !important;
    border-color: #dc3545 !important;
  }
`;

const MockTest = () => {
  const { user } = useContext(AuthContext);
  const params = useParams();
  const test_code = params.test_code;
  const course_url = params.course_url;
  const test_type = params.test_type ;
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [examStatus, setExamStatus] = useState("unattempted");
  const [instructions, setInstructions] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [results, setResults] = useState(null);
  // Add a state to control the results modal
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  // Map of quiz_id → {attempted, selected_option, marked, correct}
  const attemptsMap = React.useMemo(() => {
    const res = {};
    attempts.forEach(obj => {
      const key = Object.keys(obj)[0];
      res[key] = obj[key];
    });
    return res;
  }, [attempts]);

  // Derived counts (a question counts as attempted once an option is selected)
  const attemptedCount = React.useMemo(
    () => Object.values(attemptsMap).filter(a => a && (a.attempted || a.selected_option)).length,
    [attemptsMap]
  );
  const markedCount = React.useMemo(
    () => Object.values(attemptsMap).filter(a => a && a.marked).length,
    [attemptsMap]
  );

const [testQuestions, setTestQuestions] = useState(null);
const [duration, setDuration] = useState(null);
  // Fetch test status and instructions
  const checkTest = useCallback(async () => {
    const form = new FormData();
    if (user) form.append("session", user.SessionId);
    form.append("test_type", test_type);
    form.append("course_url", course_url);
    form.append("test_code", test_code);
    try {
      const res = await axios.post(ENDPOINT + "check-test-attempt2.php", form, {
        ...SecurityHeaders,
        validateStatus: status => status >= 200 && status < 500,
      });
      setChecking(false);
      if (res.data.status === "success") {
        setInstructions(res.data.data);
        setTestQuestions(res.data.total_questions);
       setDuration(res.data.data[0].duration );  
        setExamStatus(res.data.data.exam_status || "unattempted");
      } else {
        setExamStatus("error");
      }
    } catch (error) {
      setChecking(false);
      setExamStatus("error");
      console.error("CheckTest error:", error);
    }
  }, [user, test_code]);

  // console.log("Exam Status:", examStatus, "Time Left:", timeLeft);

  // Load a quiz question
  const loadQuiz = useCallback(async ({ quiz_id }) => {
    setLoading(true);
    try {
      const res = await GetCourseQuiz({
        payload: {
          user: user.SessionId,
          test_code,
          course_url,
          quiz_id: quiz_id || null,
        },
      });
      if (res.status === "success") {
        setQuiz(res.quiz || null);
        setTimeLeft(prev =>
          prev === null
            ? (res.quiz.duration || 0) * 60
            : prev
      );

        setQuestions(res.questions || []);
        setAttempts(res.attempts || []);
        window.scrollTo(0, 0);
      } else {
        setQuiz(null);
        setExamStatus("error");
      }
    } catch (error) {
      console.error("LoadQuiz error:", error);
      setQuiz(null);
      setExamStatus("error");
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [user, test_code, course_url]);

  // Start test
  const startTest = useCallback(async () => {
    if (examStatus !== "unattempted") return;

    const data = new FormData();
    data.append("session", user.SessionId);
    data.append("test_id", test_code);
    data.append("course_url", course_url);
    try {
      const res = await axios.post(ENDPOINT + "start-test.php", data, SecurityHeaders);
      if (res.data.status === "success" && res.data.exam_status === "unattempted") {
        await loadQuiz({ quiz_id: null });
      }
      if(res.data.exam_status === "pending" || res.data.exam_status === "completed") {
      fetchResults();
      }
    } catch (error) {
      console.error("StartTest error:", error);
      setExamStatus("error");
    } finally {
      setReloading(false);
    }
  }, [examStatus, user, test_code, course_url, loadQuiz]);

  // Submit answer (no change allowed after submission)
  // Record a selection locally + silently on the server. Does NOT reveal whether
  // it is correct, and lets the user change the answer before final submit.
  // Correctness is only shown after Submit (in the results modal).
  const submitAnswer = useCallback(async ({ option }) => {
    if (!quiz) return;
    const qid = quiz.quiz_id;
    setQuiz(prev => (prev ? { ...prev, attempted: true, selected_option: option } : null));
    setAttempts(prev => {
      const next = [...prev];
      const idx = questions.findIndex(q => String(q) === String(qid));
      const existing = idx !== -1 && next[idx] ? next[idx][qid] : {};
      if (idx !== -1) {
        next[idx] = { [qid]: { ...(existing || {}), attempted: true, selected_option: option } };
      }
      return next;
    });
    try {
      await SubmitCourseQuiz({ payload: { option, user: user.SessionId, quiz_id: qid } });
    } catch (error) {
      console.error("SubmitAnswer error:", error);
    }
  }, [quiz, questions, user]);

  // Mark/unmark question for review
  const toggleMarkQuestion = useCallback(() => {
    if (!quiz) return;
    const isMarked =
    (
      attemptsMap[quiz.quiz_id] &&
      attemptsMap[quiz.quiz_id].marked
    )
    ? attemptsMap[quiz.quiz_id].marked
    : false;
    setAttempts(prev => {
      const next = [...prev];
      const idx = questions.findIndex(qid => String(qid) === String(quiz.quiz_id));
      if (idx !== -1) {
        next[idx] = {
          [quiz.quiz_id]: {
            ...next[idx][quiz.quiz_id],
            marked: !isMarked,
          },
        };
      }
      return next;
    });
    const form = new FormData();
    form.append("session", user.SessionId);
    form.append("quiz_id", quiz.quiz_id);
    form.append("marked", isMarked ? 0 : 1);
    axios.post(ENDPOINT + "mark_quiz-test2.php", form, SecurityHeaders).catch(error => {
      console.error("MarkQuestion error:", error);
    });
  }, [quiz, questions, attemptsMap, user]);

  // Fetch test results
  const fetchResults = useCallback(async () => {
    const form = new FormData();
    form.append("session", user.SessionId);
    form.append("test_id", test_code);
    try {
      const res = await axios.post(ENDPOINT + "get-test-results.php", form, SecurityHeaders);
      if (res.data.status === "success") {
        setResults(res.data.results);
        setExamStatus("completed");
        setQuiz(null);
        setResultsModalOpen(true); // Open results modal
      }
    } catch (error) {
      console.error("FetchResults error:", error);
    }
  }, [user, test_code]);

  // Submit test
  const handleSubmitTest = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitModalOpen(false);
    const form = new FormData();
    form.append("session", user.SessionId);
    form.append("test_id", test_code);
    try {
      const res = await axios.post(ENDPOINT + "submit-test.php", form, SecurityHeaders);
      if (res.data.status === "success") {
        await fetchResults();
      }
    } catch (error) {
      console.error("SubmitTest error:", error);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, user, test_code, fetchResults]);

  // Timer logic
  useEffect(() => {
    if (timeLeft === null || examStatus !== "unattempted" || !quiz) return;
    if (timeLeft <= 0) {
      handleSubmitTest();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, examStatus, quiz, handleSubmitTest]);

  // Prevent navigation and tab switching
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (examStatus === "unattempted" && quiz && !hasSubmittedRef.current) {
        e.preventDefault();
        e.returnValue = "Leaving the page will submit the test. Are you sure?";
        hasSubmittedRef.current = true;
        handleSubmitTest();
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible" && examStatus === "unattempted" && quiz && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true;
        handleSubmitTest();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [examStatus, quiz, handleSubmitTest]);

  // Initial load
  useEffect(() => {
    checkTest();
  }, [checkTest]);

  // Navigation
  const nextQuestion = useCallback(() => {
    if (!questions || !quiz) return;
    const currentIndex = questions.indexOf(quiz.quiz_id);
    if (currentIndex < questions.length - 1) {
      loadQuiz({ quiz_id: questions[currentIndex + 1] });
    }
  }, [quiz, questions, loadQuiz]);

  const previousQuestion = useCallback(() => {
    if (!questions || !quiz) return;
    const currentIndex = questions.indexOf(quiz.quiz_id);
    if (currentIndex > 0) {
      loadQuiz({ quiz_id: questions[currentIndex - 1] });
    }
  }, [quiz, questions, loadQuiz]);

  const loadQuestion = (quiz_id) => {
    loadQuiz({ quiz_id });
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Current selected option with null check
  const currentlySelectedOption =
  quiz
    ? (
        quiz.selected_option ||
        (
          attemptsMap[quiz.quiz_id]
            ? attemptsMap[quiz.quiz_id].selected_option
            : null
        )
      )
    : null;

  return (
    <div className="min-vh-100">
      <Helmet>
        <title>Mock Tests - SiliconMinds</title>
        <meta name="description" content="SiliconMinds" />
        <style>{styles}</style>
      </Helmet>
      {reloading ? (
        <div className="d-flex justify-content-center min-vh-50 w-100 mt-5 pt-5 mb-4">
          <CircularProgress />
        </div>
      ) : (
        <div className="container vh-100  pt-3 pb-3">
          {checking ? (
            <>
              {[...Array(6)].map((_, idx) => (
                <Skeleton key={idx} className="col-lg-8 mt-2" height={60} />
              ))}
            </>
          ) : (
            <>
              { examStatus === "unattempted" && quiz ? (
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="col-lg-7 min-vh-50 border rounded-3 p-3">
                    <h2>Test Information</h2>
                    <div className="d-flex mb-4 mt-3 flex-wrap justify-content-between">
                      <p><b>Questions:</b> {questions.length}</p>
                      <p><b>Duration:</b> {instructions.duration || "NA"} mins</p>
                      <p><b>Time Left:</b> {formatTime(timeLeft)}</p>
                    </div>
                    <hr />
                    {loading ? (
                      <div className="d-flex justify-content-center min-vh-50 w-100 mt-5 pt-5 mb-4">
                        <CircularProgress />
                      </div>
                    ) : (
                      <>
                        <h6>
                          {questions.indexOf(quiz.quiz_id) + 1}. {quiz.question}
                        </h6>
                        <div className="question-options mt-4 col-lg-8 p-0">
                          {[1, 2, 3, 4].map(n => {
                            const optVal = `option${n}`;
                            if (!quiz[optVal]) return null;
                            const isSelected = currentlySelectedOption === optVal;
                            // During the test we only show what is SELECTED — never
                            // whether it is correct. Correctness is revealed after Submit.
                            return (
                              <div className="option-item mb-3" key={optVal}>
                                <button
                                  onClick={() => submitAnswer({ option: optVal })}
                                  className={`btn w-100 text-start ${isSelected ? "selected-option" : "btn-outline-dark"}`}
                                >
                                  {quiz[optVal]}{isSelected ? "  ✓ (Selected)" : ""}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        <div className="d-flex align-items-center justify-content-between mt-3">
                          {questions.indexOf(quiz.quiz_id) > 0 && (
                            <Button variant="primary" onClick={previousQuestion}>
                              Previous
                            </Button>
                          )}
                       <Button
variant={
  attemptsMap[quiz.quiz_id] &&
  attemptsMap[quiz.quiz_id].marked
    ? "warning"
    : "outline-warning"
}
>
{
 attemptsMap[quiz.quiz_id] &&
 attemptsMap[quiz.quiz_id].marked
 ? "Unmark"
 : "Mark for Review"
}
                          </Button>
                          {questions.indexOf(quiz.quiz_id) >= questions.length - 1 ? (
                            <Button variant="primary" onClick={() => setSubmitModalOpen(true)}>
                              Submit Test
                            </Button>
                          ) : (
                            <Button variant="primary" onClick={nextQuestion}>
                              Next
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="col-lg-4 col-12 col-md-4 mt-3 border rounded-3 p-3">
                    <h4>Questions</h4>
                    <hr />
                    <div className="d-flex flex-wrap quiz-options-container overflow-y h-70">
                      {questions.map((item, index) => {
                        // Determine if this question was attempted and if the answer was correct
                        const attempt = attemptsMap[item] || {};
                        const answered = attempt.attempted || attempt.selected_option;
                        let btnClass = "btn option-button rounded-circle ";
                        // neutral colors only — do NOT reveal correctness during the test
                        if (attempt.marked) {
                          btnClass += "bg-warning text-dark";
                        } else if (answered) {
                          btnClass += "btn-primary text-light";
                        } else {
                          btnClass += "btn-outline-secondary";
                        }
                        return (
                          <button
                            key={item}
                            disabled={parseInt(quiz.quiz_id) === item}
                            onClick={() => loadQuestion(item)}
                            className={btnClass}
                          >
                            {index + 1}
                          </button>
                        );
                      })}
                    </div>
                    <div className="d-flex mt-3 justify-content-between">
                      <div className="text-center">
                        <button className="btn text-white bg-warning">{markedCount}</button>
                        <p className="small">Marked</p>
                      </div>
                      <div className="text-center">
                        <button className="btn text-white bg-success">{attemptedCount}</button>
                        <p className="small">Attempted</p>
                      </div>
                      <div className="text-center">
                        <button className="btn text-white bg-primary">{questions.length - attemptedCount}</button>
                        <p className="small">Unseen</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : examStatus === "unattempted" ? (
                <div className="border rounded-4 shadow-lg p-4 mt-5 col-lg-6 mx-auto bg-white text-center">
                  <h2 className="mb-3" style={{ color: "#27e89d", fontWeight: 700 }}>
                    <i className="bi bi-info-circle" style={{ marginRight: 8 }}></i>
                    Test Instructions
                  </h2>
                  <hr style={{ borderTop: "2px solid #27e89d33", margin: "18px 0" }} />
                  <div className="d-flex justify-content-between mb-3">
                    <div>
                      <span className="fw-bold" style={{ color: "#222" }}>Total Questions:</span>
                      <span className="ms-2" style={{ color: "#27e89d", fontWeight: 600 }}>{testQuestions}</span>
                    </div>
                    <div>
                      <span className="fw-bold" style={{ color: "#222" }}>Duration:</span>
                      <span className="ms-2" style={{ color: "#27e89d", fontWeight: 600 }}>{duration} mins</span>
                    </div>
                  </div>
                  <div className="instructions text-start px-2">
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        The test carries a total of <b>{testQuestions}</b> questions.
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        The test comprises of <b>{testQuestions}</b> weighted questions.
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        Question types: <b>Multiple choice</b>, <b>Match the following</b>, <b>Arrange in order</b>, <b>Fill in the blank</b>.
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        <b>Minimum 60% marks</b> are required for eligibility.
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        <b>All questions are mandatory.</b>
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        <b>Only one attempt</b> is allowed for this test.
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        <b>No negative marking.</b>
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-dot text-primary"></i>
                        <span className="text-danger fw-bold">Do not refresh, go back, or switch tabs during the test.</span>
                      </li>
                    </ul>
                  </div>
                  <Alert severity="error" className="text-start mt-3">
                    <span className="fw-bold">Ensure a stable internet connection.</span> Navigating away or refreshing will submit the test automatically.
                  </Alert>
                  <Button
                    variant="primary"
                    className="mt-4 px-5 py-2"
                    style={{ borderRadius: 24, fontWeight: 600, fontSize: "1.1rem" }}
                    onClick={startTest}
                    disabled={reloading}
                  >
                    {reloading ? <CircularProgress size={24} /> : "Start Test"}
                  </Button>
                </div>
              ) : examStatus === "completed" ? (
                <div className="text-center">
                  <img
                    className="col-lg-4 col-7 mb-3"
                    src={testCompletedImage}
                    alt="Skill exam"
                  />
                  <h2>Test Already Attempted</h2>
                  <p>You have already attempted this test.</p>
                  <Button variant="primary" onClick={fetchResults}>
                    View Results
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <h2>Error Loading Test</h2>
                  <p>An error occurred while loading the test. Please try again.</p>
                  <Button variant="primary" onClick={() => window.location.reload()}>
                    Retry
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <Modal show={submitModalOpen} onHide={() => setSubmitModalOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Submit Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to submit the test?</p>
          <div className="modal-summary">
            <p><strong>Total Questions:</strong> {questions.length}</p>
            <p><strong>Attempted:</strong> {attemptedCount}</p>
            <p><strong>Marked for Review:</strong> {markedCount}</p>
            <p><strong>Time Left:</strong> {formatTime(timeLeft)}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSubmitModalOpen(false)}>
            Return to Test
          </Button>
          <Button variant="primary" onClick={handleSubmitTest} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Test"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Results Modal */}
      <Modal show={resultsModalOpen} onHide={() => setResultsModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Test Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {results && (
            <div>
              <div className="d-flex justify-content-center gap-3 mb-3 flex-wrap">
                <div className="text-center p-3 shadow-sm rounded-3 col-lg-3 col-4" >
                  <p className="" style={{ color: "#888", fontWeight: 500 }}>Total Questions</p>
                  <h4 style={{ color: "#222", fontWeight: 700 }}>{results.total_questions}</h4>
                </div>
                <div className="text-center p-3 shadow-sm rounded-3 col-lg-3 col-4" >
                  <p className="" style={{ color: "#888", fontWeight: 500 }}>Correct Answers</p>
                  <h4 style={{ color: "#27e89d", fontWeight: 700 }}>{results.correct_count}</h4>
                </div>
                <div className="text-center p-3 shadow-sm rounded-3 col-lg-3 col-4" 
                >
                  <p className="" style={{ color: "#888", fontWeight: 500 }}>Score</p>
                  <h4 style={{ color: "#27e89d", fontWeight: 700 }}>{results.score}%</h4>
                </div>
              </div>
              <div className="mt-4 text-left" style={{
                background: "#f8fff8",
                borderRadius: 18,
                boxShadow: "0 2px 8px #27e89d11",
                padding: "0.3rem 0.3rem",
              }}>
                <h5 style={{ color: "#27e89d", fontWeight: 600, marginBottom: 16 }}>Question-wise Analysis</h5>
                <div className="overflow-auto" style={{ maxHeight: 400 }}>
                  {results.questions.map((q, idx) => (
                    <div
                      key={q.quiz_id}
                      className="border p-3 mb-3"
                      style={{
                        borderRadius: 12,
                        background:
                          q.correct
                            ? "linear-gradient(90deg,#eafff3 60%,#f8fff8 100%)"
                            : "linear-gradient(90deg,#fff0f0 60%,#fff 100%)",
                        border: q.correct
                          ? "1.5px solid #27e89d55"
                          : "1.5px solid #ff4d4f33",
                        boxShadow: q.correct
                          ? "0 2px 8px #27e89d11"
                          : "0 2px 8px #ff4d4f11",
                      }}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <span
                          style={{
                            display: "inline-block",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: q.correct ? "#27e89d" : "#ff4d4f",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "center",
                            lineHeight: "28px",
                            marginRight: 12,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <strong style={{ color: "#222" }}>{q.question}</strong>
                      </div>
                      <div className="mb-1">
                        <span style={{ color: "#888" }}>Your Answer: </span>
                        {q.selected_option ? (
                          <span
                            style={{
                              color: q.correct ? "#27e89d" : "#ff4d4f",
                              fontWeight: 600,
                            }}
                          >
                            {q.selected_option}
                          </span>
                        ) : (
                          <span style={{ color: "#aaa" }}>Not attempted</span>
                        )}
                      </div>
                      <div className="mb-1">
                        <span style={{ color: "#888" }}>Correct Answer: </span>
                        <span style={{ color: "#27e89d", fontWeight: 600 }}>
                          {q.correct_answer}
                        </span>
                      </div>
                      <div>
                        <span
                          style={{
                            color: q.correct ? "#27e89d" : "#ff4d4f",
                            fontWeight: 600,
                          }}
                        >
                          {q.correct ? "Correct" : "Incorrect"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setResultsModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MockTest;